import { quarantineConflictingActivityLineages } from './job';
import {
    activityIdentitiesEqual,
    createEmptyActivitySession,
    type ActivityIdentity,
    type ActivitySession,
} from './models';
import { parseStoredActivityItems } from './storage-item';
import {
    boundActivitySession,
    MAX_STORED_ITEMS,
    MAX_TOMBSTONES,
} from './storage-limits';
import {
    hasDuplicates,
    isRecord,
    parseActivityKey,
    parseArray,
    parseDismissalTombstoneMetadataArray,
    parseIdentity,
} from './storage-validation';

export const ACTIVITY_STORAGE_VERSION = 2;
const STORAGE_PREFIX = 'qpx:activity';
const ACTIVITY_STORAGE_KEY_PATTERN =
    /^qpx:activity:v[1-9]\d*:user:([^:]+):org:[^:]+$/;

interface StoredActivityEnvelope {
    readonly version: typeof ACTIVITY_STORAGE_VERSION;
    readonly identity: ActivityIdentity;
    readonly session: ActivitySession;
}

export function activityStorageKey(identity: ActivityIdentity): string {
    return `${STORAGE_PREFIX}:v${ACTIVITY_STORAGE_VERSION}:user:${encodeURIComponent(identity.userId)}:org:${encodeURIComponent(identity.organisationId)}`;
}

export function loadActivitySession(
    storage: Storage | null,
    identity: ActivityIdentity
): ActivitySession {
    if (!storage) {
        return createEmptyActivitySession();
    }

    try {
        const serialized = storage.getItem(activityStorageKey(identity));
        return serialized
            ? decodeActivitySession(serialized, identity)
            : createEmptyActivitySession();
    } catch {
        return createEmptyActivitySession();
    }
}

export function saveActivitySession(
    storage: Storage | null,
    identity: ActivityIdentity,
    session: ActivitySession
): void {
    if (!storage) return;

    const envelope: StoredActivityEnvelope = {
        version: ACTIVITY_STORAGE_VERSION,
        identity,
        session: boundActivitySession(session),
    };
    const key = activityStorageKey(identity);

    try {
        storage.setItem(key, JSON.stringify(envelope));
    } catch {
        try {
            storage.removeItem(key);
        } catch {
            // An unavailable browser store already behaves as an empty session.
        }
    }
}

export function clearActivitySession(
    storage: Storage | null,
    identity: ActivityIdentity
): void {
    if (!storage) return;

    try {
        storage.removeItem(activityStorageKey(identity));
    } catch {
        // An unavailable browser store already behaves as an empty session.
    }
}

export function clearActivitySessionsForUser(
    storage: Storage | null,
    userId: string
): void {
    if (!storage) return;

    try {
        const encodedUserId = encodeURIComponent(userId);
        const keysToRemove: string[] = [];

        for (let index = 0; index < storage.length; index += 1) {
            const key = storage.key(index);
            const match = key?.match(ACTIVITY_STORAGE_KEY_PATTERN);
            if (key && match?.[1] === encodedUserId) keysToRemove.push(key);
        }

        for (const key of keysToRemove) storage.removeItem(key);
    } catch {
        // An unavailable browser store already behaves as an empty session.
    }
}

export function decodeActivitySession(
    serialized: string,
    identity: ActivityIdentity
): ActivitySession {
    try {
        return decodeEnvelope(JSON.parse(serialized), identity);
    } catch {
        return createEmptyActivitySession();
    }
}

function decodeEnvelope(
    value: unknown,
    identity: ActivityIdentity
): ActivitySession {
    if (!isRecord(value) || value['version'] !== ACTIVITY_STORAGE_VERSION) {
        return createEmptyActivitySession();
    }

    const storedIdentity = parseIdentity(value['identity']);
    if (!storedIdentity || !activityIdentitiesEqual(storedIdentity, identity)) {
        return createEmptyActivitySession();
    }

    return parseSession(value['session']) ?? createEmptyActivitySession();
}

function parseSession(value: unknown): ActivitySession | null {
    if (!isRecord(value)) return null;

    const itemsValue = value['items'];
    const tombstonesValue = value['dismissalTombstones'];
    if (
        !Array.isArray(itemsValue) ||
        itemsValue.length > MAX_STORED_ITEMS ||
        !Array.isArray(tombstonesValue) ||
        tombstonesValue.length > MAX_TOMBSTONES
    ) {
        return null;
    }

    const parsedItems = parseArray(itemsValue, parseStoredActivityItems);
    const tombstones = parseArray(tombstonesValue, parseActivityKey);
    const tombstoneMetadata = parseDismissalTombstoneMetadataArray(
        value['dismissalTombstoneMetadata']
    );
    if (!parsedItems || !tombstones || !tombstoneMetadata) return null;
    const items = parsedItems.flat();
    if (items.length > MAX_STORED_ITEMS) return null;

    const itemKeys = items.map((item) => item.key);
    const metadataKeys = tombstoneMetadata.map((metadata) => metadata.key);
    if (
        hasDuplicates(itemKeys) ||
        hasDuplicates(tombstones) ||
        hasDuplicates(metadataKeys) ||
        itemKeys.some((key) => tombstones.includes(key)) ||
        metadataKeys.some((key) => !tombstones.includes(key))
    ) {
        return null;
    }

    return {
        items: quarantineConflictingActivityLineages(items),
        dismissalTombstones: tombstones,
        dismissalTombstoneMetadata: tombstoneMetadata,
    };
}
